import type { FC } from "react";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { getBreadcrumbsHomeLabel } from "~/core/transformers/breadcrumb-list.transformer";
import { prepareSeoData } from "~/core/transformers/seo.transformer";
import { sdkContext, settingsContext } from "~/server/get-load-context";
import { STATIC_PAGE_CACHE_HEADERS } from "~/shared/cache-headers";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPagesByConstructor } from "~/widgets/pages-by-constructor/page-by-constructor.server";

import type { Route } from "./+types/$lang.info.$slug";

const createInfoPageValidator = (): ObjectSchema<{
  slug: string;
  lang: string;
}> => {
  return object({
    slug: string().required(),
    lang: string().required(),
  });
};

export const loader = (async ({ params, context, request }) => {
  const sdk = context.get(sdkContext);
  const settings = context.get(settingsContext);
  const { slug, lang } = createInfoPageValidator().validateSync(params);
  const { page, pages } = await loadPagesByConstructor(sdk, "info", lang, {
    slug,
    segment: "info",
    pageList: "whenRelatedPages",
  });

  return data(
    {
      pageData: page,
      pages,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
    },
    {
      headers: STATIC_PAGE_CACHE_HEADERS,
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({
  data: pageData,
  params,
  matches,
}) => {
  const constructorData = pageData?.pageData;
  return prepareSeoData({
    pageSeo: constructorData?.page.seo,
    fallbackSeo: constructorData?.fallbackSeo?.seo,
    originalUrl: `${pageData?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: pageData?.languages ?? [],
    matches,
    breadcrumbs: constructorData
      ? { data: constructorData, homeLabel: getBreadcrumbsHomeLabel(matches) }
      : undefined,
  });
};

const Page: FC<Route.ComponentProps> = ({
  loaderData: { pageData, pages },
}) => {
  return pageData && <PageConstructor data={pageData} pages={pages} />;
};

export default Page;
